import { ArrowRight, ArrowRightLeft, BellRing, CheckCircle2, Copy, Database, DatabaseBackup, DatabaseCheck, Download, FolderSearch, FolderSync, HardDrive, Loader2, RefreshCw, TriangleAlert, Wrench, XCircle } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { useAlerts, useOverview, useStorage } from "../api/queries";
import type { Job, ProtectionStatus } from "../api/types";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { FirstSteps } from "../help/FirstSteps";
import { ErrorState, LoadingRows, PageHead, Panel } from "../ui/Common";
import { Meter } from "../ui/Meter";
import { Pill } from "../ui/Pills";
import { QuotaBanner } from "./Storage";
import { storageMessages } from "./storage.i18n";
import { overviewMessages } from "./overview.i18n";

const segmentColor: Record<ProtectionStatus, string> = {
  protected: "var(--ok-mark)",
  stale: "var(--warn-mark)",
  never: "var(--warn-mark)",
  failed: "var(--crit-mark)",
  revoked: "var(--idle-mark)",
};

function jobIcon(j: Job) {
  if (j.status === "running" || j.status === "queued") return { tone: "info", Icon: Loader2 };
  if (j.status === "failed") return { tone: "crit", Icon: XCircle };
  if (j.status === "warning") return { tone: "warn", Icon: TriangleAlert };
  const byKind = { backup: Database, image: HardDrive, database: DatabaseBackup, dbtest: DatabaseCheck, share: FolderSync, sharetest: FolderSearch, restore: Download, replication: ArrowRightLeft, maintenance: Wrench, enrollment: CheckCircle2 };
  return { tone: "ok", Icon: byKind[j.kind] };
}

export function OverviewPage() {
  const { orgId = "" } = useParams();
  const navigate = useNavigate();
  const q = useOverview(orgId);
  const alerts = useAlerts(orgId);
  const storage = useStorage(orgId);
  const t = useT(overviewMessages);
  const ts = useT(storageMessages);
  const tc = useT(common);
  const f = useFormat();

  if (q.isPending) return <><PageHead title={t.title} /><div className="panel"><LoadingRows rows={8} /></div></>;
  if (q.isError) return <ErrorState error={q.error} />;
  const o = q.data;
  const c = o.counts;
  const active = c.total - c.revoked;
  const order: ProtectionStatus[] = ["protected", "stale", "never", "failed", "revoked"];

  return (
    <>
      <PageHead
        title={t.title}
        lede={t.agentsOnline(c.online, c.total)}
        actions={
          <>
            <Link className="btn" to={`/o/${orgId}/activity`}><RefreshCw aria-hidden="true" /> {t.activity}</Link>
            <Link className="btn btn-primary" to={`/o/${orgId}/add-machines`}>{t.addMachines}</Link>
          </>
        }
      />

      <QuotaBanner summary={storage.data} to={`/o/${orgId}/storage`} />

      {/* Chaveado pela organização: o "oculto" é lido uma vez por organização. */}
      <FirstSteps key={orgId} orgId={orgId} />

      <div className="grid-main-side">
        <div style={{ display: "grid", gap: 20, minWidth: 0 }}>
          {alerts.data && alerts.data.length > 0 && (
            <Panel title={<><BellRing aria-hidden="true" size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />{t.alerts}</>} sub={t.alertsSub(alerts.data.length)} bodyless>
              <ul className="attn">
                {alerts.data.map((a) => (
                  <li key={a.id}>
                    <span className={`sev ${a.severity === "critical" ? "crit" : "warn"}`} aria-hidden="true" />
                    <div className="what">
                      <span>
                        {a.severity === "critical"
                          ? <Pill tone="crit" icon={XCircle}>{t.severityCritical}</Pill>
                          : <Pill tone="warn" icon={TriangleAlert}>{t.severityWarning}</Pill>}
                      </span>
                      <strong style={{ fontFamily: "var(--font-ui)", fontWeight: 600, overflowWrap: "anywhere" }}>{a.message}</strong>
                      <span>{t.alertSince(f.ago(a.openedAt))}</span>
                    </div>
                    {a.args.machineId && (
                      <Link className="btn btn-sm" to={`/o/${orgId}/machines/${encodeURIComponent(a.args.machineId)}`}>{t.open}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </Panel>
          )}
          <Panel title={t.protection} sub={t.protectionSub}>
            <div className="protection">
              <div className="protection-figure">
                <span className="big num">{c.protected}</span>
                <span className="of">{t.protectedOf(active)}</span>
              </div>
              <div className="stackbar" role="img" aria-label={order.map((s) => `${tc.protection[s]}: ${c[s]}`).join(", ")}>
                {order.map((s) => c[s] > 0 && <span key={s} style={{ flexGrow: c[s], background: segmentColor[s] }} />)}
              </div>
              <div className="legend">
                {order.map((s) => (
                  <button key={s} type="button" onClick={() => navigate(`/o/${orgId}/machines?status=${s}`)}>
                    <span className="sw" style={{ background: segmentColor[s] }} />
                    {tc.protection[s]} <strong>{c[s]}</strong>
                  </button>
                ))}
              </div>
            </div>
          </Panel>

          <Panel
            title={t.attention}
            sub={o.attention.length ? t.attentionSub : undefined}
            actions={<Link className="btn btn-sm" to={`/o/${orgId}/machines`}>{t.allMachines} <ArrowRight aria-hidden="true" /></Link>}
            bodyless
          >
            {o.attention.length === 0 ? (
              <div className="empty"><CheckCircle2 aria-hidden="true" />{t.allProtected}</div>
            ) : (
              <ul className="attn">
                {o.attention.map((m) => (
                  <li key={m.id}>
                    <span className={`sev ${m.status === "failed" ? "crit" : "warn"}`} aria-hidden="true" />
                    <div className="what">
                      <strong>{m.hostname}</strong>
                      <span>
                        {tc.protection[m.status]} · {m.lastSuccessAt ? t.lastBackup(f.ago(m.lastSuccessAt)) : t.noBackup}
                        {m.lastError ? ` · ${m.lastError}` : ""}
                      </span>
                    </div>
                    <Link className="btn btn-sm" to={`/o/${orgId}/machines/${encodeURIComponent(m.id)}`}>{t.open}</Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title={t.recentActivity} actions={<Link className="btn btn-sm" to={`/o/${orgId}/activity`}>{t.viewAll}</Link>} bodyless>
            <ul className="feed">
              {o.recentJobs.length === 0 && <li style={{ gridTemplateColumns: "1fr" }}><span className="muted">{t.noActivity}</span></li>}
              {o.recentJobs.map((j) => {
                const { tone, Icon } = jobIcon(j);
                const secs = j.endedAt ? Math.round((new Date(j.endedAt).getTime() - new Date(j.startedAt).getTime()) / 1000) : null;
                return (
                  <li key={j.id}>
                    <span className={`ico ${tone}`}><Icon aria-hidden="true" /></span>
                    <div style={{ minWidth: 0 }}>
                      <div>
                        <strong>{tc.jobKind[j.kind]}</strong>
                        {j.hostname && <> · <span className="mono">{j.hostname}</span></>}
                        {j.bytes > 0 && <span className="muted"> · {f.bytes(j.bytes)}</span>}
                        {secs !== null && secs > 0 && <span className="muted"> · {f.duration(secs)}</span>}
                      </div>
                      <div className="ink-2" style={{ overflowWrap: "anywhere" }}>{j.message}</div>
                      {j.status === "running" && j.progress !== null && (
                        <div className="progress" role="progressbar" aria-valuenow={Math.round(j.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                          <span style={{ width: `${j.progress * 100}%` }} />
                        </div>
                      )}
                    </div>
                    <span className="when">{j.status === "running" ? t.now : f.ago(j.endedAt ?? j.startedAt)}</span>
                  </li>
                );
              })}
            </ul>
          </Panel>
        </div>

        <div style={{ display: "grid", gap: 20, minWidth: 0 }}>
          <Panel title={t.planUsage} actions={<Link className="btn btn-sm" to={`/o/${orgId}/usage`}>{t.details}</Link>}>
            <div style={{ display: "grid", gap: 10 }}>
              <div className="protection-figure">
                <span className="big num" style={{ fontSize: "2rem" }}>{o.usage.quotaBytes ? f.pct(o.usage.protectedBytes / o.usage.quotaBytes) : "—"}</span>
                <span className="of">{t.ofQuota}</span>
              </div>
              <Meter value={o.usage.protectedBytes} max={o.usage.quotaBytes} label={t.protectedAmount(f.gb(o.usage.protectedBytes))} detail={t.ofTotal(f.gb(o.usage.quotaBytes))} />
            </div>
          </Panel>

          {/* Volume contratado, sem infraestrutura: o cliente não vê pool (ADR 0021). */}
          <Panel title={t.storage} actions={<Link className="btn btn-sm" to={`/o/${orgId}/storage`}>{t.details}</Link>} bodyless>
            <div className="pool-list">
              {storage.isPending && <div style={{ padding: "4px 0" }}><LoadingRows rows={2} /></div>}
              {storage.isError && <div className="panel-body"><ErrorState error={storage.error} /></div>}
              {storage.data?.pools.length === 0 && <div className="empty">{t.noStorage}</div>}
              {storage.data?.pools.map((a) => (
                <div className="pool" key={a.id}>
                  <div className="pool-top">
                    <h3>{a.name}</h3>
                    {a.replicated && <Pill tone="info" icon={Copy}>{ts.replicated}</Pill>}
                  </div>
                  {a.quotaBytes === null ? (
                    <div className="meter-row"><span>{ts.usedNoLimit(f.bytes(a.usedBytes))}</span></div>
                  ) : (
                    <Meter value={a.usedBytes} max={a.quotaBytes} label={ts.usedOf(f.bytes(a.usedBytes), f.bytes(a.quotaBytes))} detail={ts.freeOf(f.bytes(a.freeBytes ?? 0))} />
                  )}
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
